import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Download, ArrowRight, Heart, BookOpen, FileText, Shield,
  MessageCircle, Check, AlertTriangle
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreeGuidePage = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadEssay = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/bonus/download-essay`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Breaking-The-Silence-Essay.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 lg:px-12" data-testid="free-guide-hero">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Download className="h-4 w-4" /> Free Download - No Email Required
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-5">
            "Breaking the Silence"
          </h1>
          <p className="text-base md:text-lg text-[#1A2A3A]/70 max-w-2xl mx-auto mb-4">
            A raw, honest college essay about surviving two back-to-back narcissistic relationships. Written by ThryvLife. Shared because we believe silence is the narcissist's greatest weapon.
          </p>
          <Button
            onClick={handleDownloadEssay}
            disabled={downloading}
            className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white text-lg px-8 py-6 rounded-full font-semibold shadow-lg"
            data-testid="download-essay-btn"
          >
            <Download className="mr-2 h-5 w-5" />
            {downloading ? "Downloading..." : "Download the Essay (Free PDF)"}
          </Button>
        </div>
      </section>

      {/* The Story */}
      <section className="py-16 px-6 lg:px-12 bg-white" data-testid="story-section">
        <div className="container mx-auto max-w-3xl">
          <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-4">My Story</p>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A2A3A] mb-6">
            Two Relationships. Two Narcissists. One Survivor.
          </h2>
          <div className="space-y-5 text-[#1A2A3A]/70 leading-relaxed">
            <p>
              The first relationship started with fireworks. He was everything I thought I wanted: charming, attentive, full of promises. The love bombing was intoxicating. But charm has an expiration date when it is used as a tool of manipulation.
            </p>
            <p>
              Slowly, the compliments turned into criticisms. The attention turned into surveillance. By the time I realized what was happening, I had lost myself. The cheating was the final straw, but it was the gaslighting that nearly broke me. He told me I was imagining things. He told me I was crazy. He told me nobody would ever believe me.
            </p>
            <p>
              When I finally left, I thought the worst was over. I was wrong.
            </p>
            <p>
              My second relationship was a mirror image of the first, except more sophisticated. She knew exactly which wounds had not healed, and she pressed on them. The manipulation, the lies told with such conviction that I questioned my own sanity. She isolated me from friends, controlled our finances, and used my past trauma as leverage.
            </p>
            <p>
              Two back-to-back narcissistic relationships can break a person or remake them. <strong className="text-[#1A2A3A]">I chose to be remade.</strong>
            </p>
            <p>
              This essay, which I wrote in college, was the first time I put my story on paper. It was terrifying. But it was also the beginning of my freedom. I am sharing it with you because if even one person reads it and thinks "maybe I am not alone," then every tear was worth it.
            </p>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-16 px-6 lg:px-12 bg-[#FDFBF7]" data-testid="offer-section">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A2A3A] mb-8 text-center">
            More Free Tools to Help You Right Now
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: MessageCircle, title: "AI Recovery Companion", desc: "Talk to Hope, our empathetic AI companion. Available 24/7. Completely free.", link: "/chat", cta: "Chat Now" },
              { icon: FileText, title: "15 Ways to Stop a Narcissist", desc: "Our signature PDF guide with proven strategies. Download instantly.", link: "/bonus", cta: "Get the Guide" },
              { icon: Shield, title: "Emergency Resources", desc: "Arkansas hotlines, state & federal laws, and crisis support.", link: "/resources", cta: "View Resources" },
            ].map((item, i) => (
              <Card key={i} className="bg-white rounded-xl shadow-card border border-[#E5E7EB]/50 p-6" data-testid={`offer-card-${i}`}>
                <CardContent className="p-0 text-center">
                  <div className="w-12 h-12 rounded-xl bg-[#4FB3D4]/10 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="h-6 w-6 text-[#4FB3D4]" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#1A2A3A]/60 mb-4">{item.desc}</p>
                  <Link to={item.link}>
                    <Button variant="outline" className="rounded-full px-5 py-2 text-sm border-2 border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5">
                      {item.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upgrade CTA */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-[#1A2A3A] to-[#2D4356]" data-testid="upgrade-section">
        <div className="container mx-auto max-w-3xl text-center">
          <Heart className="h-8 w-8 text-[#FFB7B2] mx-auto mb-4" />
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
            Ready for the Full Recovery Toolkit?
          </h2>
          <p className="text-white/70 mb-6 max-w-xl mx-auto">
            The essay is just the beginning. Our complete program includes the e-novella, AI companion, daily affirmations, legal resources, and so much more. Starting at just $37/month.
          </p>
          <Link to="/checkout">
            <Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white text-lg px-8 py-6 rounded-full font-semibold" data-testid="upgrade-cta-btn">
              See All Plans <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Disclaimer Footer */}
      <div className="bg-[#FDFBF7] py-4 px-6 text-center">
        <p className="text-xs text-[#1A2A3A]/40">
          This content is for educational purposes only. We do not diagnose, treat, or cure any medical or mental health conditions. If you need immediate help, call 988 or 1-800-799-7233.
        </p>
      </div>

      {/* Footer */}
      <footer className="py-10 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-6">
              <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">Home</Link>
              <Link to="/resources" className="text-white/60 hover:text-white text-sm transition-colors">Resources</Link>
              <Link to="/chat" className="text-white/60 hover:text-white text-sm transition-colors">AI Companion</Link>
            </nav>
            <p className="text-white/40 text-xs">&copy; 2025 ThryvLife</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FreeGuidePage;
